import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useStandardRole } from "./useStandardRole";
import { useModuleFeatureAccess } from "./useTenantFeatureAccess";
import { useUserClinicIds } from "./useUserClinics";
import {
  applyPermissionCeiling,
  getClinicRolesForAccessCeiling,
  getEffectivePermissions,
  isModuleKey,
  type ModulePermission as RoleModulePermission,
} from "@/lib/role-permissions";
export type ModuleType = 
  | 'cases' 
  | 'tasks' 
  | 'inventory' 
  | 'reports' 
  | 'patients' 
  | 'assistant_logbook' 
  | 'call_logbook' 
  | 'radiology' 
  | 'price_chart' 
  | 'treatment_planning' 
  | 'treatment_tracking' 
  | 'vendor_invoice_management' 
  | 'consultant_logbook' 
  | 'billing' 
  | 'appointments' 
  | 'invisalign_tracking' 
  | 'implant_tracking' 
  | 'oral_surgery_tracking' 
  | 'endo_tracking' 
  | 'prostho_tracking' 
  | 'perio_tracking' 
  | 'pedo_tracking'
  | 'general_dentistry_tracking'
  | 'clinical_visits'
  | 'clinical_audit'
  | 'dues_tracker'
  | 'braces_tracking'
  | 'prescriptions'
  | 'settings_management'
  | 'settings_clinical'
  | 'settings_lab'
  | 'settings_radiology'
  | 'settings_integrations'
  | 'equipment_logbook'
  | 'documents'
  | 'emi_tracking'
  | 'financial_audit'
  | 'consent_forms'
  | 'medical_records'
  | 'post_care_instructions'
  | 'legal_documents'
  | 'travel_reimbursement'
  | 'settings_payouts'
  | 'audit_trail';

interface ModulePermission {
  id: string;
  user_id: string;
  module: string; // Using string to accommodate new enum values from DB
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
  created_at: string;
  updated_at: string;
}

interface ModulePermissions {
  cases?: ModulePermission;
  tasks?: ModulePermission;
  inventory?: ModulePermission;
  reports?: ModulePermission;
  patients?: ModulePermission;
  assistant_logbook?: ModulePermission;
  call_logbook?: ModulePermission;
  radiology?: ModulePermission;
  price_chart?: ModulePermission;
  treatment_planning?: ModulePermission;
  treatment_tracking?: ModulePermission;
  vendor_invoice_management?: ModulePermission;
  consultant_logbook?: ModulePermission;
  billing?: ModulePermission;
  appointments?: ModulePermission;
  invisalign_tracking?: ModulePermission;
  implant_tracking?: ModulePermission;
  oral_surgery_tracking?: ModulePermission;
  endo_tracking?: ModulePermission;
  prostho_tracking?: ModulePermission;
  perio_tracking?: ModulePermission;
  pedo_tracking?: ModulePermission;
  general_dentistry_tracking?: ModulePermission;
  clinical_visits?: ModulePermission;
  clinical_audit?: ModulePermission;
  dues_tracker?: ModulePermission;
  braces_tracking?: ModulePermission;
  prescriptions?: ModulePermission;
  settings_management?: ModulePermission;
  settings_clinical?: ModulePermission;
  settings_lab?: ModulePermission;
  settings_radiology?: ModulePermission;
  settings_integrations?: ModulePermission;
  equipment_logbook?: ModulePermission;
  documents?: ModulePermission;
  emi_tracking?: ModulePermission;
  financial_audit?: ModulePermission;
  consent_forms?: ModulePermission;
  medical_records?: ModulePermission;
  post_care_instructions?: ModulePermission;
  legal_documents?: ModulePermission;
  travel_reimbursement?: ModulePermission;
  settings_payouts?: ModulePermission;
  audit_trail?: ModulePermission;
}

export const useModulePermissions = (userId?: string) => {
  const { user } = useAuth();
  const targetUserId = userId || user?.id;

  return useQuery({
    queryKey: ["module-permissions", targetUserId],
    queryFn: async () => {
      if (!targetUserId) return null;

      const { data, error } = await supabase
        .from("user_module_permissions")
        .select("*")
        .eq("user_id", targetUserId);

      if (error) throw error;
      
      const permissions: ModulePermissions = {
        cases: data?.find(p => p.module === 'cases'),
        tasks: data?.find(p => p.module === 'tasks'),
        inventory: data?.find(p => p.module === 'inventory'),
        reports: data?.find(p => p.module === 'reports'),
        patients: data?.find(p => p.module === 'patients'),
        assistant_logbook: data?.find(p => p.module === 'assistant_logbook'),
        call_logbook: data?.find(p => p.module === 'call_logbook'),
        radiology: data?.find(p => p.module === 'radiology'),
        price_chart: data?.find(p => p.module === 'price_chart'),
        treatment_planning: data?.find(p => p.module === 'treatment_planning'),
        treatment_tracking: data?.find(p => p.module === 'treatment_tracking'),
        vendor_invoice_management: data?.find(p => p.module === 'vendor_invoice_management'),
        consultant_logbook: data?.find(p => p.module === 'consultant_logbook'),
        billing: data?.find(p => p.module === 'billing'),
        appointments: data?.find(p => p.module === 'appointments'),
        invisalign_tracking: data?.find(p => p.module === 'invisalign_tracking'),
        implant_tracking: data?.find(p => p.module === 'implant_tracking'),
        oral_surgery_tracking: data?.find(p => (p.module as string) === 'oral_surgery_tracking'),
        endo_tracking: data?.find(p => (p.module as string) === 'endo_tracking'),
        prostho_tracking: data?.find(p => (p.module as string) === 'prostho_tracking'),
        perio_tracking: data?.find(p => (p.module as string) === 'perio_tracking'),
        pedo_tracking: data?.find(p => (p.module as string) === 'pedo_tracking'),
        general_dentistry_tracking: data?.find(p => (p.module as string) === 'general_dentistry_tracking'),
        clinical_visits: data?.find(p => (p.module as string) === 'clinical_visits'),
        clinical_audit: data?.find(p => (p.module as string) === 'clinical_audit'),
        dues_tracker: data?.find(p => (p.module as string) === 'dues_tracker'),
        braces_tracking: data?.find(p => (p.module as string) === 'braces_tracking'),
        prescriptions: data?.find(p => (p.module as string) === 'prescriptions'),
        settings_management: data?.find(p => (p.module as string) === 'settings_management'),
        settings_clinical: data?.find(p => (p.module as string) === 'settings_clinical'),
        settings_lab: data?.find(p => (p.module as string) === 'settings_lab'),
        settings_radiology: data?.find(p => (p.module as string) === 'settings_radiology'),
        settings_integrations: data?.find(p => (p.module as string) === 'settings_integrations'),
        equipment_logbook: data?.find(p => (p.module as string) === 'equipment_logbook'),
        documents: data?.find(p => (p.module as string) === 'documents'),
        emi_tracking: data?.find(p => (p.module as string) === 'emi_tracking'),
        financial_audit: data?.find(p => (p.module as string) === 'financial_audit'),
        consent_forms: data?.find(p => (p.module as string) === 'consent_forms'),
        medical_records: data?.find(p => (p.module as string) === 'medical_records'),
        post_care_instructions: data?.find(p => (p.module as string) === 'post_care_instructions'),
        legal_documents: data?.find(p => (p.module as string) === 'legal_documents'),
        travel_reimbursement: data?.find(p => (p.module as string) === 'travel_reimbursement'),
        settings_payouts: data?.find(p => (p.module as string) === 'settings_payouts'),
        audit_trail: data?.find(p => (p.module as string) === 'audit_trail'),
      };
      
      return permissions;
    },
    enabled: !!targetUserId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// Helper hook to check if user can access a specific module
// This now checks BOTH tenant feature access AND user module permissions
export const useCanAccessModule = (module: ModuleType) => {
  const { data: permissions, isLoading: permissionsLoading } = useModulePermissions();
  const { data: standardRole, isLoading: roleLoading } = useStandardRole();
  const { hasModuleFeature, isLoading: featureLoading } = useModuleFeatureAccess(module);
  const { clinics: userClinics, isLoading: clinicsLoading } = useUserClinicIds();
  
  const isAdmin = standardRole?.isOwner ?? false;
  const isSuperAdmin = standardRole?.isSuperAdmin ?? false;
  const knownModule = isModuleKey(module) ? module : null;
  const accessRoleAssignments = standardRole?.assignments?.length
    ? standardRole.assignments.map((assignment) => ({
        standardRole: assignment.standardRole,
        legacyRole: assignment.legacyRole,
      }))
    : (userClinics ?? []).map((clinic) => ({
        legacyRole: clinic.role,
      }));
  const clinicRoles = getClinicRolesForAccessCeiling(accessRoleAssignments);
  const roleCeiling = knownModule
    ? getEffectivePermissions(isAdmin || isSuperAdmin ? "admin" : "staff", clinicRoles)[knownModule]
    : undefined;
  const rawPermission = permissions?.[module];
  const cappedPermission =
    knownModule && !isAdmin && !isSuperAdmin
      ? applyPermissionCeiling(rawPermission as RoleModulePermission | undefined, roleCeiling)
      : rawPermission;
  
  // Super admins bypass all checks - don't wait for feature/permission loading
  // This prevents infinite loading spinners for super admin users
  const isLoading = isSuperAdmin ? roleLoading : (permissionsLoading || roleLoading || featureLoading || clinicsLoading);
  
  // Regular admins still need the tenant feature to be enabled
  // Regular users need tenant feature, user permission, and role-profile permission.
  const userHasPermission = cappedPermission?.can_view ?? false;
  const canAccess = isSuperAdmin || (hasModuleFeature && (isAdmin || userHasPermission));
  
  // Determine why access was denied
  const deniedReason: "no_feature" | "no_permission" | null = canAccess
    ? null
    : !hasModuleFeature
      ? "no_feature"
      : "no_permission";

  return {
    canAccess,
    deniedReason,
    canCreate: isSuperAdmin || (hasModuleFeature && (isAdmin || (cappedPermission?.can_create ?? false))),
    canEdit: isSuperAdmin || (hasModuleFeature && (isAdmin || (cappedPermission?.can_edit ?? false))),
    canDelete: isSuperAdmin || (hasModuleFeature && (isAdmin || (cappedPermission?.can_delete ?? false))),
    isLoading,
    // Expose for debugging/UI
    hasModuleFeature,
    userHasPermission: isAdmin || userHasPermission,
  };
};
